import {
  AuthenticationData,
  login,
  logout,
  readMe,
  refresh,
} from "@directus/sdk";
import { useEffect, useState } from "react";
import { GaripalliDirectusClient, isTokenExpired } from "../directus/client";
import { debounce, toMilliseconds } from "../libs/utils";

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing?: boolean;
  error: string | null;
}

export const useAuth = (
  client: GaripalliDirectusClient,
  opts?: { verbose?: boolean; traceKey: string }
) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isRefreshing: false,
    error: null,
  });

  const [authData, setAuthData] = useState<AuthenticationData | null>(null);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  // const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Check if user is already authenticated on mount

  const updateToken = (authData: AuthenticationData | null) => {
    console.trace("Updating token", JSON.stringify(authData));
    setAuthData(authData);
    storeAuthData(authData);
  };

  const storeAuthData = (data: AuthenticationData | null) => {
    if (data) {
      localStorage.setItem("@garipalli/auth_data", JSON.stringify(data));
    } else {
      localStorage.removeItem("@garipalli/auth_data");
    }
  };

  const loadStoredAuthData = () => {
    const data = localStorage.getItem("@garipalli/auth_data");
    if (!data) {
      setAuthData(null);
    } else {
      try {
        const parsedData = JSON.parse(data);
        setAuthData(parsedData);
      } catch (error) {
        console.error("Failed to load auth data", error);
        setAuthData(null);
      }
    }
  };

  const checkAuthStatus = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Try to get current user info
      const user = await client.request(readMe());
      console.log("Authenticated user", user);

      setAuthState({
        user: user as User,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error("Not authenticated", error);
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        error: String(error),
      }));
      if (isTokenExpired(error)) {
        await refreshAccessToken();
      }
    }
  };

  const loginUser = async (credentials: LoginCredentials) => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Login with email and password
      const response = await client.request(
        login(
          {
            email: credentials.email,
            password: credentials.password,
          },
          { mode: "json" }
        )
      );

      updateToken(response);

      // Get user info after successful login
      const user = await client.request(readMe());
      console.log("Authenticated user", user);

      setAuthState({
        user: user as User,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return { success: true };
    } catch (error: any) {
      const errorMessage =
        error?.errors?.[0]?.message || error?.message || "Login failed";

      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      return { success: false, error: errorMessage };
    }
  };

  const logoutUser = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      await client.request(logout());

      updateToken(null);
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      return { success: true };
    } catch (error: any) {
      const errorMessage =
        error?.errors?.[0]?.message || error?.message || "Logout failed";

      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      return { success: false, error: errorMessage };
    }
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    if (!authData?.access_token) {
      console.error("No access token available");
      return null;
    } else if (!authData?.refresh_token) {
      console.error("No refresh token available");
      return null;
    } else if (!(await client.getToken())) {
      await client.setToken(authData.access_token);
    }

    try {
      setAuthState((prev) => ({ ...prev, isRefreshing: true }));
      const data = await client.request(
        refresh({
          mode: "json",
          refresh_token: authData.refresh_token,
        })
      );

      updateToken(data);
      return data.access_token;
    } catch (error) {
      if (isTokenExpired(error)) {
        updateToken(null);
        setAuthState((prev) => ({
          ...prev,
          user: null,
          isLoading: false,
          isRefreshing: false,
          isAuthenticated: false,
        }));
        return null;
      }

      throw error;
    } finally {
      setAuthState((prev) => ({ ...prev, isRefreshing: false }));
    }
  };

  const debouncedRefreshToken = debounce(refreshAccessToken, 200);

  const clearError = () => {
    setAuthState((prev) => ({ ...prev, error: null }));
  };

  useEffect(() => {
    if (opts?.verbose) {
      console.log("Auth data changed", authData);
    }

    if (authData?.access_token) {
      client.getToken().then(async (token) => {
        if (token !== authData.access_token) {
          await client.setToken(authData.access_token);

          if (opts?.verbose) {
            console.log(
              `[${opts?.traceKey}] Set new access token in client`,
              await client.getToken()
            );
          }

          await checkAuthStatus();

          setRefreshTimer((prev) => {
            clearTimeout(prev);
            return setTimeout(async () => {
              if (!authState.isRefreshing) {
                await debouncedRefreshToken();
              }
            }, toMilliseconds({ minutes: 3, seconds: 0 }));
          });
        }
      });
    } else {
      setRefreshTimer((prev) => {
        clearTimeout(prev);
        return undefined;
      });
      client.setToken(null);
    }
    return () => {
      clearTimeout(refreshTimer);
    };
  }, [authData?.access_token]);

  useEffect(() => {
    loadStoredAuthData();
  }, []);

  return {
    state: authState,
    data: authData,
    loginUser,
    logoutUser,
    loadStoredAuthData,
    refreshAccessToken: debouncedRefreshToken,
    checkAuthStatus,
    clearError,
  };
};
